import React, { useRef, useMemo, memo } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';
import { 
  ChevronLeft, 
  ChevronRight, 
  Play,
  BarChart3,
  Users,
  Trophy
} from 'lucide-react';
import { useContainerSize } from '../hooks/useContainerSize';
import { useUniversalTextSizing, getUniversalTextConfig } from '../utils/cardTitleSizing';
import AnimatedLogo from './AnimatedLogo';
import PrivacyLockScreen from './PrivacyLockScreen';

// Type definitions
interface JudgeProfile {
  name?: string;
  title?: string;
  defaultNote?: string;
  assignedPosition?: number;
  [key: string]: any;
}

interface CurrentRoutine {
  routineNumber: number | string;
  number?: number | string; // Display number from PerformanceSchedule
  name: string;
  performerName?: string;
  studioCode?: string;
  studioName?: string;
  studio?: string;
  category?: string;
  division?: string;
  type?: string;
  ageGroup?: string;
  [key: string]: any;
}

interface SizeConfig {
  containerPadding: string;
  logoSize: 'sm' | 'md' | 'lg';
  titleSize: string;
  routineNumberSize: string;
  performerNameSize: string;
  routineNameSize: string;
  tagPadding: string;
  tagTextSize: string;
  buttonIconSize: number;
  statusTextSize: string;
  maxPerformerLength: number;
  maxRoutineLength: number;
  gap: string;
}

interface UnifiedStationHeaderProps {
  // Station Type - determines the layout and features
  stationType?: 'judge' | 'tabulator';
  
  // Judge Station Props (only used when stationType is 'judge')
  judgeProfile?: JudgeProfile | string;
  effectiveJudgeId?: string;
  judgePosition?: string;
  connected?: boolean;
  socketStatus?: string;
  competitionCode?: string;
  isBlurred?: boolean;
  onToggleBlur?: () => void;
  isReconnecting?: boolean;
  reconnectAttempts?: number;
  
  // Tabulator Station Props (only used when stationType is 'tabulator')
  tabulatorTitle?: string;
  tabulatorSubtitle?: string;
  tabulatorConnected?: boolean;
  tabulatorStatus?: string;
  tabulatorRoomId?: string;
  tabulatorIsBlurred?: boolean;
  onTabulatorToggleBlur?: () => void;
  onTabulatorScoreSummary?: () => void;
  onTabulatorViewAwards?: () => void;
  tabulatorJudgesCount?: number;
  tabulatorIsReconnecting?: boolean;
  tabulatorReconnectAttempts?: number;
  
  // Routine Props (used by both)
  currentRoutine?: CurrentRoutine | null;
  canNavigatePrevious?: boolean;
  canNavigateNext?: boolean;
  onNavigatePrevious?: () => void;
  onNavigateNext?: () => void;
  timerProgress?: number;
  
  className?: string;
}

// Generate profile avatar with photo or initials
const getProfileAvatar = (judgeProfile: JudgeProfile | string, className: string = 'w-8 h-8'): React.ReactElement => {
  // If judge is just an ID string (legacy format)
  if (typeof judgeProfile === 'string') {
    // Extract initials or just use first character if no name format
    const initial = judgeProfile.charAt(0).toUpperCase();
    
    // Generate a deterministic color based on judgeId
    const hue = (judgeProfile.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % 360);
    const bgColor = `hsl(${hue}, 70%, 30%)`;
    
    return (
      <div className={`${className} rounded-full flex items-center justify-center text-white font-bold text-sm relative border-2 border-white/20 shadow-lg`} style={{ backgroundColor: bgColor }}>
        {initial}
      </div>
    );
  }
  
  // If we have a judge object with profile information
  if (judgeProfile && typeof judgeProfile === 'object') {
    // If we have a photo URL, use it
    if (judgeProfile.photoUrl || judgeProfile.photo) {
      const photoUrl = judgeProfile.photoUrl || judgeProfile.photo;
      
      return (
        <div className={`${className} rounded-full bg-gray-300 overflow-hidden relative border-2 border-white/20 shadow-lg`}>
          <img 
            src={photoUrl}
            alt={judgeProfile.name || "Judge"} 
            className="w-full h-full object-cover"
            onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
              const target = e.target as HTMLImageElement;
              if (!target) return;
              
              target.onerror = null;
              target.style.display = 'none';
              
              // Generate a color based on name or id
              const id = judgeProfile.id || judgeProfile.name || "Judge";
              const hue = (id.split('').reduce((acc: number, char: string) => acc + char.charCodeAt(0), 0) % 360);
              const bgColor = judgeProfile.color || `hsl(${hue}, 70%, 30%)`;
              
              // Get initial from name
              const initial = (judgeProfile.name || "J").charAt(0).toUpperCase();
              
              // Create fallback element with initial
              const parent = target.parentNode as HTMLElement;
              if (parent) {
                const fallback = document.createElement('div');
                fallback.className = 'w-full h-full rounded-full flex items-center justify-center text-white font-bold text-sm';
                fallback.style.backgroundColor = bgColor;
                fallback.textContent = initial;
                parent.appendChild(fallback);
              }
            }}
          />
        </div>
      );
    }
    
    // If no photo but we have a name, use initials
    const initial = judgeProfile.name ? judgeProfile.name.charAt(0).toUpperCase() : "J";
    
    // Generate a color based on name or id
    const id = judgeProfile.id || judgeProfile.name || "Judge";
    const hue = (id.split('').reduce((acc: number, char: string) => acc + char.charCodeAt(0), 0) % 360);
    const bgColor = judgeProfile.color || `hsl(${hue}, 70%, 30%)`;
    
    return (
      <div className={`${className} rounded-full flex items-center justify-center text-white font-bold text-sm relative border-2 border-white/20 shadow-lg`} style={{ backgroundColor: bgColor }}>
        {initial}
      </div>
    );
  }
  
  // Default fallback
  return (
    <div className={`${className} rounded-full flex items-center justify-center text-white font-bold text-sm relative border-2 border-white/20 shadow-lg`} style={{ backgroundColor: "hsl(215, 70%, 30%)" }}>
      J
    </div>
  );
};

/**
 * UnifiedStationHeader Component
 * 
 * Unified header component that works for both judge and tabulator stations.
 * Combines station header with routine information to maximize space.
 * Use stationType prop to switch between 'judge' and 'tabulator' modes.
 */
  const UnifiedStationHeader: React.FC<UnifiedStationHeaderProps> = ({
  // Station Type
  stationType = 'judge',
  
  // Judge Station Props
  judgeProfile,
  effectiveJudgeId,
  judgePosition,
  connected = false,
  socketStatus = 'Disconnected',
  competitionCode,
  isBlurred = false,
  onToggleBlur,
  isReconnecting = false,
  reconnectAttempts = 0,
  
  // Tabulator Station Props
  tabulatorTitle = 'TABULATOR STATION',
  tabulatorSubtitle = 'Competition Management',
  tabulatorConnected = false,
  tabulatorStatus = 'Disconnected',
  tabulatorRoomId,
  tabulatorIsBlurred = false,
  onTabulatorToggleBlur,
  onTabulatorScoreSummary,
  onTabulatorViewAwards,
  tabulatorJudgesCount = 0,
  tabulatorIsReconnecting = false,
  tabulatorReconnectAttempts = 0,
  
  // Routine Props
  currentRoutine,
  canNavigatePrevious = false,
  canNavigateNext = false,
  onNavigatePrevious,
  onNavigateNext,
  timerProgress = 0,
  
  className = ''
}) => {
  const { theme, setTheme } = useTheme();
  const isDarkMode = theme === 'dark';

  // Helper to get routine name from routine object
  const getRoutineName = (routine: any) => {
    if (!routine) return "Unknown";
    
    return routine["Routine Name"] || 
           routine.routineName || 
           routine.name || 
           "Unknown";
  };
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Use universal text sizing system
  const { textSize } = useUniversalTextSizing();
  const universalConfig = getUniversalTextConfig(textSize);
  
  // Container-responsive design
  const { containerSize } = useContainerSize(containerRef);
  const effectiveContainerSize = containerSize || 'medium';
  
  // Container-responsive size configuration using universal text sizing
  const sizeConfig: SizeConfig = useMemo(() => {
    const configs: Record<string, SizeConfig> = {
      small: {
        containerPadding: universalConfig.sectionPadding,
        logoSize: 'sm',
        titleSize: universalConfig.cardTitle,
        routineNumberSize: universalConfig.cardTitle,
        performerNameSize: universalConfig.cardTitle,
        routineNameSize: universalConfig.cardTitle,
        tagPadding: universalConfig.badgeSize,
        tagTextSize: universalConfig.smallText,
        buttonIconSize: 14,
        statusTextSize: universalConfig.captionText,
        maxPerformerLength: 20,
        maxRoutineLength: 25,
        gap: universalConfig.spacing
      },
      medium: {
        containerPadding: universalConfig.cardPadding,
        logoSize: 'md',
        titleSize: universalConfig.cardTitle,
        routineNumberSize: universalConfig.cardTitle,
        performerNameSize: universalConfig.cardTitle,
        routineNameSize: universalConfig.cardTitle,
        tagPadding: universalConfig.badgeSize,
        tagTextSize: universalConfig.smallText,
        buttonIconSize: 16,
        statusTextSize: universalConfig.captionText,
        maxPerformerLength: 30,
        maxRoutineLength: 35,
        gap: universalConfig.spacing
      },
      large: {
        containerPadding: universalConfig.cardPadding,
        logoSize: 'md',
        titleSize: universalConfig.cardTitle,
        routineNumberSize: universalConfig.cardTitle,
        performerNameSize: universalConfig.cardTitle,
        routineNameSize: universalConfig.cardTitle,
        tagPadding: universalConfig.badgeSize,
        tagTextSize: universalConfig.smallText,
        buttonIconSize: 18,
        statusTextSize: universalConfig.captionText,
        maxPerformerLength: 40,
        maxRoutineLength: 45,
        gap: universalConfig.spacingLarge
      }
    };
    return configs[effectiveContainerSize] || configs.medium;
  }, [effectiveContainerSize, universalConfig]);
  

  // Function to truncate text based on container size
  const truncatePerformerName = (text: string | undefined): string => {
    if (!text) return '';
    return text.length > sizeConfig.maxPerformerLength ? text.substring(0, sizeConfig.maxPerformerLength) + '...' : text;
  };
  
  const truncateRoutineName = (text: string | undefined): string => {
    if (!text) return '';
    return text.length > sizeConfig.maxRoutineLength ? text.substring(0, sizeConfig.maxRoutineLength) + '...' : text;
  };

  // Check if performer name exists
  const hasPerformerName = currentRoutine?.performerName && currentRoutine.performerName.trim() !== '';

  return (
    <motion.div 
      ref={containerRef}
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`relative rounded-2xl overflow-hidden bg-white/5 backdrop-blur-xl border border-white/20 ${className}`}
    >
      {/* Timer Progress Indicator */}
      {timerProgress > 0 && (
        <div className="absolute inset-0 overflow-hidden">
          <motion.div 
            className="absolute inset-0 bg-gradient-to-r from-pink-500/30 via-purple-500/30 to-transparent"
            style={{ 
              width: `${timerProgress + 15}%`,
            }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          />
        </div>
      )}
      
      <div className={`relative ${sizeConfig.containerPadding}`}>
        {/* Single Compact Row Layout */}
        <div className={`flex items-center justify-between ${sizeConfig.gap}`}>
          
          {/* Left Section: Logo + Station Info + Status */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <AnimatedLogo 
              className="" 
              size={sizeConfig.logoSize} 
              style={{}} 
              alt="Company Logo" 
            />
            <div className="flex flex-col">
              <motion.div 
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1 }}
                className={`${sizeConfig.titleSize} font-black leading-none`}
              >
                {stationType === 'judge' ? (
                  <>
                    <span className="text-white">JUDGE </span>
                    <span className="bg-gradient-to-r from-pink-400 via-purple-400 to-orange-400 bg-clip-text text-transparent">STATION</span>
                  </>
                ) : (
                  <>
                    <span className="text-white">TABULATOR </span>
                    <span className="bg-gradient-to-r from-pink-400 via-purple-400 to-orange-400 bg-clip-text text-transparent">STATION</span>
                  </>
                )}
              </motion.div>
              <motion.div 
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.2 }}
                className="flex items-center gap-1.5 mt-0.5"
              >
                {stationType === 'judge' ? (
                  // Judge station status indicators
                  <>
                    <span className={`flex items-center gap-1 ${sizeConfig.statusTextSize} px-1.5 py-0.5 rounded-full backdrop-blur-xl ${
                      connected
                        ? "bg-green-500/20 border border-green-400/40 text-green-300"
                        : isReconnecting
                        ? "bg-yellow-500/20 border border-yellow-400/40 text-yellow-300"
                        : "bg-red-500/20 border border-red-400/40 text-red-300"
                    }`}>
                      <div 
                        className={`w-1 h-1 rounded-full ${
                          connected ? 'bg-green-400' : 
                          isReconnecting ? 'bg-yellow-400 animate-pulse' : 
                          'bg-red-400'
                        }`}
                      />
                      {connected ? socketStatus : 
                       isReconnecting ? `Reconnecting... (${reconnectAttempts + 1}/5)` : 
                       'Disconnected'}
                    </span>
                    {competitionCode && (
                      <span className={`${sizeConfig.statusTextSize} px-1.5 py-0.5 rounded-full bg-purple-500/20 border border-purple-400/40 text-purple-300 backdrop-blur-xl`}>
                        {competitionCode}
                      </span>
                    )}
                    {((typeof judgeProfile === 'object' && judgeProfile?.name) || (typeof judgeProfile === 'string') || effectiveJudgeId) && (
                      <div className="flex items-center gap-1">
                        <span className={`flex items-center gap-1 ${sizeConfig.statusTextSize} px-1.5 py-0.5 rounded-full bg-slate-500/20 border border-slate-400/40 text-slate-200 backdrop-blur-xl`}>
                          <span className="font-medium">
                            {typeof judgeProfile === 'object' ? judgeProfile?.name : judgeProfile || effectiveJudgeId}
                          </span>
                          {judgePosition && (
                            <>
                              <span className="text-slate-400">•</span>
                              <span className="font-bold text-cyan-300">
                                #{judgePosition}
                              </span>
                            </>
                          )}
                        </span>
                        {typeof judgeProfile === 'object' && judgeProfile.isTeacherCritique && (
                          <span className="flex-shrink-0 px-1 py-0.5 text-[10px] font-bold bg-orange-500/20 text-orange-300 rounded-full border border-orange-400/30">
                            TC
                          </span>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  // Tabulator station status indicators
                  <>
                    <span className={`flex items-center gap-1 ${sizeConfig.statusTextSize} px-1.5 py-0.5 rounded-full backdrop-blur-xl ${
                      tabulatorConnected
                        ? "bg-green-500/20 border border-green-400/40 text-green-300"
                        : tabulatorIsReconnecting
                        ? "bg-yellow-500/20 border border-yellow-400/40 text-yellow-300"
                        : "bg-red-500/20 border border-red-400/40 text-red-300"
                    }`}>
                      <div 
                        className={`w-1 h-1 rounded-full ${
                          tabulatorConnected ? 'bg-green-400' : 
                          tabulatorIsReconnecting ? 'bg-yellow-400 animate-pulse' : 
                          'bg-red-400'
                        }`}
                      />
                      {tabulatorConnected ? tabulatorStatus : 
                       tabulatorIsReconnecting ? `Reconnecting... (${tabulatorReconnectAttempts + 1}/5)` : 
                       'Disconnected'}
                    </span>
                    <span className={`flex items-center gap-1 ${sizeConfig.statusTextSize} px-1.5 py-0.5 rounded-full bg-blue-500/20 border border-blue-400/40 text-blue-300 backdrop-blur-xl`}>
                      <Users size={10} />
                      <span className="font-semibold">{tabulatorJudgesCount}</span>
                      <span>Judge{tabulatorJudgesCount !== 1 ? 's' : ''}</span>
                    </span>
                    {tabulatorRoomId && (
                      <span className={`${sizeConfig.statusTextSize} px-1.5 py-0.5 rounded-full bg-purple-500/20 border border-purple-400/40 text-purple-300 backdrop-blur-xl`}>
                        {tabulatorRoomId}
                      </span>
                    )}
                    {tabulatorSubtitle && (
                      <span className={`${sizeConfig.statusTextSize} text-white/60`}>
                        • {tabulatorSubtitle}
                      </span>
                    )}
                  </>
                )}
              </motion.div>
            </div>
          </div>

          {/* Center Section: Current Routine Info */}
          {currentRoutine ? (
            <div className="flex items-center justify-center gap-3 flex-1 min-w-0">
              {/* Previous button - closer to content */}
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={onNavigatePrevious}
                disabled={!canNavigatePrevious}
                className={`p-1.5 rounded-full transition-all backdrop-blur-xl ${
                  canNavigatePrevious
                    ? 'bg-white/10 hover:bg-white/20 text-white border border-white/20'
                    : 'opacity-30 cursor-not-allowed text-white/40'
                }`}
                aria-label="Previous routine"
              >
                <ChevronLeft size={sizeConfig.buttonIconSize} />
              </motion.button>

              {/* Routine Info - Two Row Layout */}
              <div className="flex flex-col gap-1 min-w-0">
                {/* Top Row: Routine Number, Routine Name, and conditionally Performer Name/Studio Name based on station type */}
                <div className="flex items-center justify-center gap-1 min-w-0 mb-1 bg-gradient-to-r from-pink-400 via-purple-400 to-orange-400 bg-clip-text text-transparent font-black text-xl drop-shadow-sm">
                  <span className={`${sizeConfig.routineNumberSize} flex-shrink-0`}>
                    #{currentRoutine.routineNumber}
                  </span>
                  
                  <span className="flex-shrink-0 font-bold">-</span>
                  <span className={`${sizeConfig.routineNameSize} truncate flex-shrink`}>
                    {currentRoutine.name}
                  </span>
                  
                  {/* Show performer name for all stations when available */}
                  {hasPerformerName && (
                    <>
                      <span className={`${sizeConfig.performerNameSize} truncate`}>
                        ({truncatePerformerName(currentRoutine.performerName)})
                      </span>
                    </>
                  )}
                  
                  {/* For judges: don't show studio info to keep display clean */}
                  
                  {/* For non-judges: show full studio name */}
                  {stationType !== 'judge' && currentRoutine.studioName && (
                    <>
                      <span className="flex-shrink-0 font-bold">•</span>
                      <span className={`${sizeConfig.routineNameSize} truncate flex-shrink min-w-0`} title={currentRoutine.studioName}>
                        {currentRoutine.studioName}
                      </span>
                    </>
                  )}
                </div>
                
                {/* Bottom Row: Routine Tags - Match PerformanceSchedule order and colors */}
                <div className="flex items-center gap-1 justify-center">
                  {/* Age Group - First in order */}
                  {currentRoutine.ageGroup && (
                    <span className={`${sizeConfig.tagPadding} rounded-full ${sizeConfig.tagTextSize} font-medium ${
                      isDarkMode
                        ? "bg-yellow-900/30 text-yellow-300"
                        : "bg-yellow-100 text-yellow-800"
                    } backdrop-blur-xl`}>
                      {currentRoutine.ageGroup}
                    </span>
                  )}
                  {/* Category - Second in order */}
                  {currentRoutine.category && (
                    <span className={`${sizeConfig.tagPadding} rounded-full ${sizeConfig.tagTextSize} font-medium ${
                      isDarkMode
                        ? "bg-purple-900/30 text-purple-300"
                        : "bg-purple-100 text-purple-800"
                    } backdrop-blur-xl`}>
                      {currentRoutine.category}
                    </span>
                  )}
                  {/* Division - Third in order */}
                  {currentRoutine.division && (
                    <span className={`${sizeConfig.tagPadding} rounded-full ${sizeConfig.tagTextSize} font-medium ${
                      isDarkMode
                        ? "bg-blue-900/30 text-blue-300"
                        : "bg-blue-100 text-blue-800"
                    } backdrop-blur-xl`}>
                      {currentRoutine.division}
                    </span>
                  )}
                  {/* Type - Fourth in order (if needed) */}
                  {currentRoutine.type && currentRoutine.type !== 'routine' && (
                    <span className={`${sizeConfig.tagPadding} rounded-full ${sizeConfig.tagTextSize} font-medium ${
                      isDarkMode
                        ? "bg-green-900/30 text-green-300"
                        : "bg-green-100 text-green-800"
                    } backdrop-blur-xl`}>
                      {currentRoutine.type}
                    </span>
                  )}
                </div>
              </div>

              {/* Next button - closer to content */}
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={onNavigateNext}
                disabled={!canNavigateNext}
                className={`p-1.5 rounded-full transition-all backdrop-blur-xl ${
                  canNavigateNext
                    ? 'bg-white/10 hover:bg-white/20 text-white border border-white/20'
                    : 'opacity-30 cursor-not-allowed text-white/40'
                }`}
                aria-label="Next routine"
              >
                <ChevronRight size={sizeConfig.buttonIconSize} />
              </motion.button>
            </div>
          ) : (
            // Compact empty state
            <div className="flex items-center gap-2 flex-1 justify-center text-white/60">
              <Play className="w-4 h-4 text-purple-400 opacity-50" />
              <span className={`${sizeConfig.titleSize} font-medium`}>No Routine Selected</span>
            </div>
          )}

          {/* Right Section: Controls + Avatar */}
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {stationType === 'judge' && (
              <>
                <PrivacyLockScreen
                  isLocked={isBlurred}
                  onToggle={onToggleBlur}
                  className="!p-1.5"
                  iconSize={16}
                />

                {/* Judge Avatar - compact */}
                {((typeof judgeProfile === 'object' && judgeProfile?.name) || (typeof judgeProfile === 'string') || effectiveJudgeId) && (
                  <motion.div 
                    initial={{ opacity: 0, x: 5 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: 0.3 }}
                    className="cursor-pointer group"
                    title={`${typeof judgeProfile === 'object' ? judgeProfile?.name : judgeProfile || effectiveJudgeId}${typeof judgeProfile === 'object' && judgeProfile?.title ? ` - ${judgeProfile.title}` : ''}`}
                  >
                    <div className="relative transform transition-transform duration-200 group-hover:scale-105">
                      {(judgeProfile || effectiveJudgeId) && getProfileAvatar(judgeProfile || effectiveJudgeId || '', 'w-10 h-10')}
                    </div>
                  </motion.div>
                )}
              </>
            )}
            {stationType === 'tabulator' && (
              <>
                <PrivacyLockScreen
                  isLocked={tabulatorIsBlurred}
                  onToggle={onTabulatorToggleBlur}
                  className="!p-1.5"
                  iconSize={16}
                />
                {onTabulatorScoreSummary && (
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    onClick={onTabulatorScoreSummary}
                    className="p-1.5 rounded-full bg-gradient-to-r from-pink-500/20 to-purple-500/20 backdrop-blur-xl border border-pink-400/40 hover:from-pink-500/30 hover:to-purple-500/30 transition-all duration-300 text-pink-200 hover:text-pink-100"
                    aria-label="Show score summary"
                    title="Score Summary"
                  >
                    <BarChart3 size={16} />
                  </motion.button>
                )}
                {onTabulatorViewAwards && (
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    onClick={onTabulatorViewAwards}
                    className="p-1.5 rounded-full bg-gradient-to-r from-purple-500/20 to-blue-500/20 backdrop-blur-xl border border-purple-400/40 hover:from-purple-500/30 hover:to-blue-500/30 transition-all duration-300 text-purple-200 hover:text-purple-100"
                    aria-label="View Awards"
                    title="View Awards"
                  >
                    <Trophy size={16} />
                  </motion.button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default memo(UnifiedStationHeader);
